import { apiClient } from './client';

export interface PendingValidation {
  pinId: string;
  pinIndex: number;
  requestedBy: string;
  requestedByDisplayName: string;
  requestedAt: string;
}

export interface ValidationRequest {
  alreadyPending?: boolean;
  validation: PendingValidation;
}

export interface ValidationApproval {
  approved: boolean;
  hasChallenge: boolean;
  challengeType?: 'riddle' | 'hex_code' | 'numerical';
  huntComplete?: boolean;
  nextPinIndex?: number;
}

export interface ValidationRejection {
  rejected: boolean;
  newFoundCount: number;
  itemCount: number;
}

/**
 * Service for pin validation operations - hider referee feature
 */
export const validationService = {
  /**
   * Get pending validation for a hunt (if any)
   */
  async getPendingValidation(huntId: string): Promise<PendingValidation | null> {
    const response = await apiClient.get<{ pendingValidation: PendingValidation | null }>(
      `/hunts/${huntId}/validation/pending`
    );
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get pending validation');
    }
    return response.data.pendingValidation;
  },

  /**
   * Request pin validation from hiders (hunter action)
   */
  async requestValidation(huntId: string, pinId: string): Promise<ValidationRequest> {
    const response = await apiClient.post<ValidationRequest>(
      `/hunts/${huntId}/pins/${pinId}/validation/request`
    );
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to request validation');
    }
    return response.data;
  },

  /**
   * Approve pin validation (hider action)
   */
  async approveValidation(huntId: string, pinId: string): Promise<ValidationApproval> {
    const response = await apiClient.post<ValidationApproval>(
      `/hunts/${huntId}/pins/${pinId}/validation/approve`
    );
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to approve validation');
    }
    return response.data;
  },

  /**
   * Reject pin validation - item not found (hider action)
   */
  async rejectValidation(huntId: string, pinId: string): Promise<ValidationRejection> {
    const response = await apiClient.post<ValidationRejection>(
      `/hunts/${huntId}/pins/${pinId}/validation/reject`
    );
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to reject validation');
    }
    return response.data;
  },
};
